import { getStatusGetQueryKey, useStatusGet } from '@/api/generated';
import type { BannerContent } from '@cotality/marketplace-contracts/endpoints/banner';

/**
 * Reads the sidebar banner from `GET /api/status` (`banner` field). Anonymous-
 * safe — `/api/status` is reachable by every user, unlike `/api/flags`. Shares
 * the status query cache with `usePublicFlag`/`useMaintenanceStatus` (same key
 * + options → one request, no extra polling cadence). Returns `banner: null`
 * while loading, on error, or when no banner is set (fail-safe — render
 * nothing).
 *
 * `isInitialLoad` follows the same latch semantics as `usePublicFlag`: true
 * ONLY before the first settle (success or error), never again — callers use
 * it as a gate to avoid flashing the banner card while the query is in flight.
 */
export interface UseBannerResult {
  banner: BannerContent | null;
  isInitialLoad: boolean;
}

export function useBanner(): UseBannerResult {
  const query = useStatusGet({
    query: {
      queryKey: getStatusGetQueryKey(),
      staleTime: 15_000,
      refetchInterval: 30_000,
      refetchIntervalInBackground: false,
    },
  });
  return {
    // The mutator unwraps `{ success, data }`, so `query.data` is
    // `StatusGet200Data` (`{ banner, maintenance, publicFlags }`); `banner` is
    // already `BannerContent | null` on the contract.
    banner: query.data?.banner ?? null,
    // React Query v5 — `isFetched` latches true on the first settle and never
    // reverts; `isLoading && !isFetched` is true only before that first settle.
    isInitialLoad: query.isLoading && !query.isFetched,
  };
}
